/**
 * prescribingFlags.tsx — WHAT THIS ORDER STILL OWES A PRESCRIBER, on screen.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: prescribing truth
 * (2026-09-12).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE INCIDENT THIS BELONGS TO. Until 2026-09-12 approving an order AUTO-ISSUED
 * a month-1 prescription inside the approve click: orders-service derived it
 * from the ORDER — `medicationName` = the raw product key, no variant, no dose —
 * rendered the signed PDF and emailed the pharmacy. The clinician never
 * prescribed at all, so the prescribe screen (and the strength picker added
 * beside it) was never reached on the lane that was actually producing
 * documents. Two real patients collected the wrong strength.
 *
 * orders deleted the auto-issue. Approval now captures month 1 exactly as it
 * always did and then STOPS, leaving the order at `approved` with
 * `correctlyPrescribed = false`, and the clinician prescribes it through the
 * formulary picker. That is the change this file makes visible: an order the
 * server is deliberately holding must never look like an order nobody has to
 * touch.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * TWO DIFFERENT JOBS, TWO DIFFERENT BUTTONS — AND THEY ARE SERVER FACTS
 *
 *   `awaitingPrescription`        — approved, PAID, and NO prescription exists.
 *                                   The steady state of every newly approved
 *                                   order from today. NOT temporary. → the
 *                                   ordinary Prescribe flow, with the picker.
 *
 *   `needsPrescriptionReapproval` — a prescription EXISTS and records no
 *                                   prescriber-chosen strength: the finite v1
 *                                   backlog the deleted auto-issue minted.
 *                                   ⚠️ TEMPORARY → the re-prescribe modal.
 *
 * They arrive on the staff order from orders-service, from ONE call to its
 * `prescribingStatus`, so they are mutually exclusive by construction and this
 * file never has to cross-check them.
 *
 * ⚠️ NOTHING HERE IS DERIVED FROM THE PRESCRIPTION ITEMS, AND THAT IS THE WHOLE
 * DISCIPLINE. The portal could look at `item.variant == null` and reach the same
 * answer most of the time — the deleted correction surface did exactly that —
 * and "most of the time" is how a browser-side predicate drifts away from the
 * server gate it is supposed to mirror. The gate that stops a document reaching
 * Pharmacy Express is `OrderCase.correctlyPrescribed`, written from one
 * predicate in orders-service; these two flags are read from that same
 * predicate. The portal RELAYS them (R41: the server is the control).
 *
 * AN ABSENT KEY MEANS NO FLAG, and that is deliberate rather than careless. The
 * fail-CLOSED decision — nothing dispenses unless something actively vouched for
 * it — lives on the server, where it stops a send. Here an absent key means an
 * orders revision that predates these fields is answering, and inventing work
 * for a clinician out of a key that never arrived would put phantom cards in
 * front of Reneal during a rolling deploy. Absent = say nothing; the server
 * still refuses the send.
 * ═════════════════════════════════════════════════════════════════════════════
 *
 * R28: no medicine name appears here. R19: nothing is logged.
 */
import type { CSSProperties, ReactNode } from 'react';
import type { StaffOrder } from '../api/types';
import { Alert, Badge } from '../ui/kit';

/** The pair, as this portal reads it. Both always answered; never optional. */
export interface PrescribingFlags {
  /** Approved + paid, no prescription at all → the ordinary Prescribe flow. */
  awaitingPrescription: boolean;
  /** ⚠️ TEMPORARY — a prescription exists with no recorded strength. */
  needsPrescriptionReapproval: boolean;
}

const NOTHING_OWED: PrescribingFlags = { awaitingPrescription: false, needsPrescriptionReapproval: false };

/**
 * Read the pair off a staff order.
 *
 * `=== true` rather than a truthiness test, for the same reason the server's
 * send gate is written that way: `undefined` (an older orders revision) must
 * take the "nothing claimed" branch explicitly, not fall through one.
 */
export function prescribingFlagsOf(order: StaffOrder | null | undefined): PrescribingFlags {
  if (!order) return NOTHING_OWED;
  return {
    awaitingPrescription: order.awaitingPrescription === true,
    needsPrescriptionReapproval: order.needsPrescriptionReapproval === true,
  };
}

/** Does this order owe a prescriber anything at all? */
export function owesPrescribing(flags: PrescribingFlags): boolean {
  return flags.awaitingPrescription || flags.needsPrescriptionReapproval;
}

/* ── THE WORDS ─────────────────────────────────────────────────────────────
 *
 * Exported as constants so every surface states the SAME sentence and the specs
 * pin the real string rather than a fragment of it. Three surfaces carry these
 * today (the deal drawer header, the drawer's Overview panel and the case
 * review), and three surfaces that each paraphrase is three chances to say
 * something subtly different about a prescription.
 */

/** The chip on a card whose order has no prescription yet. */
export const AWAITING_CHIP = 'Prescription owed';
/** ⚠️ TEMPORARY — the chip on a v1 backlog card. */
export const REAPPROVAL_CHIP = 'Re-approval needed';

/**
 * The Overview reason for an order that has never been prescribed.
 *
 * ACTIONABLE WORDS, NOT A STATE NAME. "Awaiting prescription" describes the row
 * in the database; this describes what the person reading it has to do, and says
 * the money part out loud because "approved and paid" is what makes it urgent.
 */
export const AWAITING_REASON =
  'This order has been approved and the patient has paid, but no prescription has been issued yet. ' +
  'Nothing goes to the pharmacy until you prescribe it. Open the Review tab, choose the strength from the licensed list and prescribe.';

/**
 * ⚠️ TEMPORARY — the Overview reason for a v1 backlog order, in the CTO's own
 * framing: the prescription was not saved correctly and needs re-saving.
 *
 * IT DOES NOT SAY "ERROR", "FAILED" OR "INVALID". A clinician reading this did
 * nothing wrong — the document was minted by a lane that never asked them
 * anything — and a card that accuses its reader is a card that gets ignored. It
 * says what is wrong with the record and what fixes it.
 */
export const REAPPROVAL_REASON =
  'The prescription on this order was not saved correctly — no medication strength was recorded on it — so it needs re-saving. ' +
  'Nothing can be dispensed against it until then. Re-prescribe it with the correct strength; no payment will be taken.';

/**
 * The one-line marker for a tab or a card, or null when nothing is owed.
 *
 * Short on purpose: it shares a slot with the medication key-alert marker on the
 * deal drawer's Review tab, and that one must always win (a double-prescribing
 * alert is a safety signal; this is a work queue).
 */
export function prescribingTabWarning(flags: PrescribingFlags): string | null {
  if (flags.needsPrescriptionReapproval) return 'Prescription needs re-saving';
  if (flags.awaitingPrescription) return 'Prescription not issued yet';
  return null;
}

/** The reason sentence for whichever flag is set, or null. */
export function prescribingReason(flags: PrescribingFlags): string | null {
  if (flags.needsPrescriptionReapproval) return REAPPROVAL_REASON;
  if (flags.awaitingPrescription) return AWAITING_REASON;
  return null;
}

/**
 * THE CARD INDICATOR — the existing Badge vocabulary, no new pill grammar.
 *
 * Tones are picked the way the rest of this estate picks them: `danger` for a
 * record that is WRONG and is blocking a dispense, `pending` (amber) for work
 * that is simply outstanding. Both carry their meaning in the WORDS — colour
 * alone never states anything here (WCAG 1.4.1).
 */
export function PrescribingFlagChip({ flags }: { flags: PrescribingFlags }): ReactNode {
  if (flags.needsPrescriptionReapproval) return <Badge tone="danger">{REAPPROVAL_CHIP}</Badge>;
  if (flags.awaitingPrescription) return <Badge tone="pending">{AWAITING_CHIP}</Badge>;
  return null;
}

/**
 * THE REASON BLOCK — the chip's sentence, for a panel with room for one.
 *
 * `role="status"` rather than `alert`: this is standing state on a screen the
 * reader has opened deliberately, and the assertive live region on these
 * surfaces belongs to the safeguarding banner.
 */
export function PrescribingReasonNotice({ flags, style }: { flags: PrescribingFlags; style?: CSSProperties }) {
  const reason = prescribingReason(flags);
  if (!reason) return null;
  return (
    <Alert tone={flags.needsPrescriptionReapproval ? 'danger' : 'warning'} role="status" style={style}>
      <strong style={{ display: 'block', fontWeight: 600 }}>
        {flags.needsPrescriptionReapproval ? REAPPROVAL_CHIP : AWAITING_CHIP}
      </strong>
      <span style={{ display: 'block', marginTop: '4px' }}>{reason}</span>
    </Alert>
  );
}
